import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useSignIn } from "@clerk/clerk-react";
import clsx from "clsx";
import { routeEndpoints } from "../../routes/routeEndpoints";
import { clerkErrorMessage } from "../../utils/clerkError";

type ForgotPasswordFormValues = {
  email: string;
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { isLoaded, signIn } = useSignIn();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>();

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    if (!isLoaded) return;

    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: data.email,
      });

      navigate(routeEndpoints.verifyResetCode);
    } catch (error) {
      setError("root", { message: clerkErrorMessage(error) });
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-sans">
      <div className="w-full max-w-xl md:ml-28 mt-9 px-4 md:px-0">
        <button
          type="button"
          onClick={() => navigate(routeEndpoints.login)}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Login
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-xl shadow-sm border border-slate-100 md:px-10 md:py-9 px-6 py-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center size-14 rounded-full bg-blue-50">
                <EnvelopeIcon className="size-6 text-blue-600" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="md:text-2xl md:font-semibold text-xl font-medium text-gray-950">
                  Reset password
                </h1>
                <p className="text-sm md:font-medium font-normal text-gray-400">
                  Enter your email to receive a 6-digit code.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-8"
            >
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-950"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="Enter your email"
                  className={clsx(
                    "w-full rounded-xl border px-4 py-3.5 text-sm outline-none focus:ring-2 placeholder:text-gray-400",
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-blue-500",
                  )}
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Must be a valid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div className="flex flex-col">
                {errors.root && (
                  <p className="mb-4 text-sm text-center text-red-500">
                    {errors.root.message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!isLoaded || isSubmitting}
                  className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending…" : "Send code"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
